/**
 * Les effets de bord : réseau, stockage, géolocalisation.
 *
 * Tout ce qui n'est pas pur passe par ici. Les composants d'interface
 * appellent ces actions et ne connaissent ni `fetch` ni `localStorage`.
 */
import {
  FOOTPRINT_IMAGE_SCAN_KM,
  NAMED_ZONES,
  NAMING_PARALLEL,
  NOSE_HISTORY_MAX,
  SCAN_MAX_RETRIES,
  STORAGE_KEYS,
  ZONES_MAX_AGE_MIN,
} from '../core/constants';
import { FRANCE_RINGS } from '../core/france';
import { ageInMinutes } from '../core/format';
import { distanceKm } from '../core/geo';
import type {
  AirSnapshot,
  FireDetection,
  FireZone,
  LatLon,
  NoseEntry,
  Place,
  SensorReading,
  UserPosition,
  WeatherSnapshot,
} from '../core/types';
import { groupIntoZones } from '../core/zones';
import { fetchBurntAreas } from '../data/effis';
import { findLatestAvailableDay, scanFrance } from '../data/fires';
import { fetchFirmsDetections } from '../data/firms';
import { pool } from '../data/http';
import { fetchAir, fetchWeather } from '../data/openmeteo';
import { createPlaceLookup, reverseGeocode, type PlaceLookup } from '../data/places';
import { fetchSensors } from '../data/sensors';
import { geolocator, preferences } from '../platform';
import type { LocateFailure } from '../platform';
import type { Store } from './store';

interface CachedBundle {
  air: AirSnapshot | null;
  weather: WeatherSnapshot | null;
  sensors: SensorReading[];
}

interface CachedZones {
  at: number;
  day: number;
  zones: FireZone[];
}

const LOCATE_MESSAGES: Readonly<Record<LocateFailure, string>> = {
  unsupported: "Ton navigateur ne sait pas te localiser. Tape ton adresse.",
  insecure:
    'Ici le navigateur bloque la localisation. Tape ton adresse, ça marche pareil.',
  denied: 'Tu as refusé la localisation. Tape ton adresse à la place.',
  unavailable: 'Localisation impossible. Tape ton adresse à la place.',
};

/**
 * Relit le journal des odeurs, en acceptant l'ancien format.
 *
 * La version monofichier stockait `{ t, smoke }`. Comme c'est une donnée
 * saisie par l'utilisateur, on la convertit au lieu de la jeter.
 */
function readNoseJournal(): NoseEntry[] {
  const raw = preferences.read<unknown[]>(STORAGE_KEYS.nose, []);
  if (!Array.isArray(raw)) return [];

  return raw.flatMap((entry): NoseEntry[] => {
    if (typeof entry !== 'object' || entry === null) return [];
    const record = entry as { at?: unknown; t?: unknown; smoke?: unknown };
    const at =
      typeof record.at === 'number'
        ? record.at
        : typeof record.t === 'number'
          ? record.t
          : null;
    if (at === null || !Number.isFinite(at)) return [];
    return [{ at, smoke: record.smoke === true }];
  });
}

export interface Actions {
  readonly places: PlaceLookup;
  /** Restaure ce qui est mémorisé, puis lance les chargements. */
  boot(): Promise<void>;
  /** Air, vent et capteurs pour la position courante. */
  refresh(): Promise<void>;
  /** Balayage satellite de toute la France. */
  runScan(): Promise<void>;
  /**
   * Périmètres de surfaces brûlées récentes, pour le contexte de la carte.
   *
   * Indépendante de `runScan` à dessein : c'est une autre source, une autre
   * grandeur, et elle ne doit jamais retarder ni influencer le verdict.
   */
  loadBurntAreas(): Promise<void>;
  setPosition(lat: number, lon: number, label: string | null): Promise<void>;
  locateMe(): Promise<void>;
  addNoseEntry(smoke: boolean): void;
  notify(text: string | null, durationMs?: number): void;
}

export function createActions(store: Store): Actions {
  const places = createPlaceLookup();

  let refreshing = false;
  let scanning = false;
  let burntLoaded = false;
  let cacheBust = 0;
  let consecutiveScanFailures = 0;
  let messageTimer: ReturnType<typeof setTimeout> | undefined;

  const notify: Actions['notify'] = (text, durationMs = 8000) => {
    clearTimeout(messageTimer);
    store.patch({ message: text });
    if (text !== null) {
      messageTimer = setTimeout(() => {
        if (store.get().message === text) store.patch({ message: null });
      }, durationMs);
    }
  };

  /**
   * Cherche le nom de commune des zones les plus proches encore anonymes.
   *
   * En parallèle, et non plus une par une : avec un plafond à 12 et des appels
   * séquentiels, une cinquantaine de polygones restaient sans nom sur la carte
   * un jour chargé. Le cache fait que seules les zones nouvelles coûtent un
   * appel d'un balayage sur l'autre.
   */
  async function nameNearestZones(): Promise<void> {
    const { position, zones } = store.get();
    const toName = [...zones]
      .filter((zone) => !zone.place)
      .sort(
        (a, b) =>
          distanceKm(position.lat, position.lon, a.lat, a.lon) -
          distanceKm(position.lat, position.lon, b.lat, b.lon),
      )
      .slice(0, NAMED_ZONES);

    if (toName.length === 0) return;

    await pool(toName, NAMING_PARALLEL, async (zone) => {
      const place = await places.resolve(zone.lat, zone.lon);
      if (!place) return;
      // On remplace la zone dans l'état pour que l'interface se redessine
      // dès qu'un nom arrive, sans attendre les autres.
      store.patch({
        zones: store.get().zones.map((candidate) =>
          candidate.id === zone.id ? { ...candidate, place } : candidate,
        ),
      });
    });
  }

  const actions: Actions = {
    places,

    async boot() {
      await preferences.hydrate();

      const savedPosition = preferences.read<UserPosition | null>(
        STORAGE_KEYS.position,
        null,
      );
      const nose = readNoseJournal();

      store.patch({
        ...(savedPosition ? { position: savedPosition } : {}),
        nose,
      });

      /*
       * On réaffiche tout de suite le dernier balayage connu, puis on en
       * relance un en arrière-plan. Sans cela, l'ouverture montrait un écran
       * vide pendant tout le temps du balayage, ce qui donnait l'impression
       * que rien ne marchait.
       */
      const cached = preferences.read<CachedZones | null>(STORAGE_KEYS.zones, null);
      if (
        cached &&
        Array.isArray(cached.zones) &&
        Number.isFinite(cached.at) &&
        ageInMinutes(cached.at, Date.now()) < ZONES_MAX_AGE_MIN
      ) {
        for (const zone of cached.zones) {
          if (zone.place) places.remember(zone.lat, zone.lon, zone.place);
        }
        store.patch({
          zones: cached.zones,
          fireDay: Number.isFinite(cached.day) ? cached.day : -1,
        });
      }

      await Promise.all([actions.refresh(), actions.runScan()]);

      // Première visite : on propose la localisation, sans jamais l'imposer.
      if (!savedPosition) void actions.locateMe();
    },

    async refresh() {
      if (refreshing) return;
      refreshing = true;
      try {
        const { position } = store.get();
        const now = Date.now();
        const [air, weather, sensors] = await Promise.allSettled([
          fetchAir(position.lat, position.lon, now),
          fetchWeather(position.lat, position.lon, now),
          fetchSensors(position.lat, position.lon),
        ]);

        // Une source en panne ne vide pas l'écran : on garde la dernière
        // valeur connue plutôt que d'afficher un blanc inexplicable.
        const cache = preferences.read<CachedBundle>(STORAGE_KEYS.cache, {
          air: null,
          weather: null,
          sensors: [],
        });

        const next = {
          air: air.status === 'fulfilled' ? air.value : cache.air,
          weather: weather.status === 'fulfilled' ? weather.value : cache.weather,
          sensors: sensors.status === 'fulfilled' ? sensors.value : (cache.sensors ?? []),
        };

        store.patch({ ...next, lastRefresh: Date.now() });
        preferences.write(STORAGE_KEYS.cache, next);
      } finally {
        refreshing = false;
      }
    },

    async runScan() {
      if (scanning) return;
      scanning = true;
      store.patch({ scanning: true });

      try {
        /*
         * FIRMS d'abord, balayage d'images ensuite.
         *
         * FIRMS apporte la confiance, la puissance radiative et l'heure réelle
         * du passage, que l'image PNG ne contient pas. Mais elle passe par une
         * fonction serveur qui n'existe que sur Cloudflare Pages : ailleurs,
         * l'appel échoue et l'on se rabat sans que l'utilisateur voie rien.
         */
        /*
         * Le balayage d'images ne renvoie que des positions. On y ajoute la
         * résolution réelle des tuiles, sans quoi le contour d'un foyer serait
         * tracé avec l'empreinte de MODIS, supposée par défaut, soit une fois et
         * demie trop large.
         */
        const withScanFootprint = (points: readonly LatLon[]): FireDetection[] =>
          points.map((point) => ({ ...point, footprintKm: FOOTPRINT_IMAGE_SCAN_KM }));

        const firms = await fetchFirmsDetections();
        if (firms) {
          store.patch({
            zones: groupIntoZones(firms.detections, FRANCE_RINGS, places.snapshot()),
            scanReport: { ok: 1, failed: 0, error: null },
            scanProgress: null,
            fireDay: 0,
          });
          await nameNearestZones();
          preferences.write(STORAGE_KEYS.zones, {
            at: Date.now(),
            day: 0,
            zones: store.get().zones,
          });
          return;
        }

        let day = store.get().fireDay;
        // Le jour publié n'est cherché qu'une fois par session.
        if (!Number.isFinite(day) || day > 0) day = await findLatestAvailableDay();
        store.patch({ fireDay: day });

        const result = await scanFrance({
          dayOffset: day,
          cacheBust,
          onProgress: (progress) => {
            store.patch({
              scanProgress: { done: progress.tilesDone, total: progress.totalTiles },
            });
            // Fin d'un jour : on montre déjà ce qu'on a trouvé.
            if (progress.dayComplete) {
              store.patch({
                zones: groupIntoZones(
                  withScanFootprint(progress.points),
                  FRANCE_RINGS,
                  places.snapshot(),
                ),
              });
            }
          },
        });

        store.patch({
          zones: groupIntoZones(withScanFootprint(result.points), FRANCE_RINGS, places.snapshot()),
          scanReport: result.report,
          scanProgress: null,
        });

        /*
         * Si aucune image n'a pu être lue, on le DIT au lieu de laisser croire
         * qu'il n'y a pas de feu, et on réessaie sans le cache du navigateur.
         */
        if (result.report.failed > 0 && result.report.ok === 0) {
          cacheBust += 1;
          consecutiveScanFailures += 1;
          const cause = result.report.error?.message ?? 'réseau';
          if (consecutiveScanFailures <= SCAN_MAX_RETRIES) {
            notify(
              `Images satellite inaccessibles (${cause}). Nouvelle tentative ` +
                `${consecutiveScanFailures} sur ${SCAN_MAX_RETRIES}.`,
              8000,
            );
            scanning = false;
            store.patch({ scanning: false });
            setTimeout(() => void actions.runScan(), 4000);
            return;
          }
          notify(
            `Images satellite toujours inaccessibles (${cause}). La qualité de l'air ` +
              'reste à jour.',
            20_000,
          );
        } else {
          consecutiveScanFailures = 0;
          if (result.report.failed > 0) {
            const total = result.report.failed + result.report.ok;
            notify(
              `${result.report.failed} image${result.report.failed > 1 ? 's' : ''} sur ` +
                `${total} illisibles. La carte peut être incomplète.`,
              8000,
            );
          }
        }

        await nameNearestZones();

        preferences.write(STORAGE_KEYS.zones, {
          at: Date.now(),
          day,
          zones: store.get().zones,
        });
      } catch (error) {
        cacheBust += 1;
        const reason = error instanceof Error ? error.message : String(error);
        notify(`La recherche de feux a échoué : ${reason}`, 9000);
      } finally {
        scanning = false;
        store.patch({ scanning: false, scanProgress: null });
      }
    },

    async loadBurntAreas() {
      /*
       * Une seule fois par session, et sans bruit. EFFIS publie ses périmètres
       * après coup, pas en continu : les interroger plus souvent n'apprendrait
       * rien. Un échec ne dit rien à l'utilisateur, parce qu'il n'y a rien à en
       * conclure : contrairement à un balayage manqué, une couche de contexte
       * absente n'induit personne en erreur.
       */
      if (burntLoaded) return;
      burntLoaded = true;
      const burnt = await fetchBurntAreas(Date.now());
      if (burnt.length > 0) store.patch({ burnt });
      else burntLoaded = false;
    },

    async setPosition(lat, lon, label) {
      const previous = store.get().position;
      const position: UserPosition = { lat, lon, label: label ?? previous.label };
      store.patch({ position });
      preferences.write(STORAGE_KEYS.position, position);

      await actions.refresh();

      // Sans libellé fourni, on cherche le nom de la ville en arrière-plan.
      if (!label) {
        const place: Place | null = await reverseGeocode(lat, lon);
        if (place) {
          const named = { lat, lon, label: place.label };
          store.patch({ position: named });
          preferences.write(STORAGE_KEYS.position, named);
        }
      }
    },

    async locateMe() {
      notify('Localisation en cours.', 4000);
      const result = await geolocator.locate();
      if (!result.ok) {
        notify(LOCATE_MESSAGES[result.reason]);
        return;
      }
      notify(null);
      await actions.setPosition(result.lat, result.lon, null);
    },

    addNoseEntry(smoke) {
      // On relit le stockage avant d'écrire : un autre onglet a peut-être
      // ajouté une entrée entre-temps.
      const entries = [{ at: Date.now(), smoke }, ...readNoseJournal()].slice(
        0,
        NOSE_HISTORY_MAX,
      );
      store.patch({ nose: entries });
      preferences.write(STORAGE_KEYS.nose, entries);
    },

    notify,
  };

  return actions;
}
